import { readReportedCompletionCount } from "./reportedCompletionCount";
import {
  ACCEPTED_REPORTING_TIME_ZONES,
  REPORTING_TIME_ZONE,
  isAcceptedReportingTimeZone,
  reportingDateOf,
} from "../utils/reportingCalendar";

// Snapshot uses the Backend reporting identities. Meta parent aggregation is
// still performed by the caller; publisher identities are never collapsed.
export function canonicalSnapshotChannel(value) {
  if (typeof value !== "string" || !value.trim()) return null;
  const key = value.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (["metricool", "community"].includes(key)) return "community";
  if (key.includes("instagram") || key === "ig") return "instagram";
  if (key.includes("facebook") || ["meta", "metasocial", "metapaid", "fb"].includes(key)) return "facebook";
  if (["tiktok", "tt"].includes(key)) return "tiktok";
  if (key.includes("youtube") || key === "yt" || key.includes("trueview") ||
      (key.includes("google") && key.includes("video"))) return "youtube";
  if (["linkedin", "linkedinads", "linkedinadvertising", "linkedinad"].includes(key)) return "linkedin";
  return key;
}

// The reporting day the backend resolves "today" in — Eastern Time — or, for
// checking an answer that states another accepted calendar, that calendar's.
export function canonicalReportingDay(now = Date.now(), timeZone = REPORTING_TIME_ZONE) {
  return reportingDateOf(now, timeZone);
}

// The end a window clamped to "today" would carry under each accepted
// calendar. While the backend moves from Pacific to Eastern, an answer is
// checked against the end for the calendar it STATES, never another's.
export function reportingEndsByTimeZone(bound, now = Date.now()) {
  return Object.fromEntries(ACCEPTED_REPORTING_TIME_ZONES.map(zone => {
    const today = canonicalReportingDay(now, zone);
    return [zone, bound && bound < today ? bound : today];
  }));
}

// The ends a window may honestly carry: the requested end (an echoed explicit
// range), or — for a window the backend clamped to its own "today" — the end
// for the calendar the answer STATES. An answer that states no calendar (an
// unpriced publisher snapshot with no canonical evidence) is calendar-unknown:
// the end for any accepted calendar is consistent with it.
export function windowEndMatches(end, timeframe, statedZone) {
  if (end == null) return true;
  if (!["number", "string"].includes(typeof end) || String(end).trim() === "") return false;
  const byZone = timeframe?.endsByTimeZone || {};
  const ends = new Set([timeframe?.end]);
  if (statedZone) ends.add(byZone[statedZone]);
  else Object.values(byZone).forEach(value => ends.add(value));
  return ends.has(String(end));
}

// The calendar an answer states for its days, from the answer itself.
export const answerTimeZone = snapshot => snapshot?.window?.timeZone ?? snapshot?.canonicalSummary?.timeframe?.timeZone ??
  snapshot?.canonicalReporting?.timeframe?.timeZone ?? null;

const dateOnly = value => {
  if (typeof value !== "string") return null;
  const day = value.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const parsed = new Date(`${day}T12:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === day ? day : null;
};

// Mirrors the published snapshot FLIGHT / FULL_DELIVERY normalization. An
// arbitrary explicit range keeps its exact bounds; min(end,today) alone would
// incorrectly accept a stale shortened response. Campaign bounds come from
// the advertiser-scoped campaign-list endpoint, not the returned metric row.
export function resolveEffectiveReportingTimeframe({ timeframe = {}, campaign = null, now = Date.now() } = {}) {
  const requested = { ...timeframe };
  const flightStart = dateOnly(campaign?.flight_start_date || campaign?.flightStartDate || campaign?.flightStart);
  const flightEnd = dateOnly(campaign?.flight_end_date || campaign?.flightEndDate || campaign?.flightEnd);
  if (!flightStart || !flightEnd) return requested;
  const publishedFull = campaign?.fullDeliveryWindow || campaign?.full_delivery_window || campaign?.reportingContract?.fullDeliveryWindow;
  const fullEnd = [flightEnd, dateOnly(campaign?.authorized_delivery_end_date || campaign?.authorizedDeliveryEndDate),
    dateOnly(publishedFull?.end)].filter(Boolean).sort().pop();
  const preset = String(timeframe.datePreset || "").toLowerCase();
  const explicit = Boolean(timeframe.start && timeframe.end);
  let effectivePreset = null;
  let bound = null;
  if (!explicit && ["flight", "full_delivery"].includes(preset)) {
    effectivePreset = preset;
    bound = preset === "full_delivery" ? fullEnd : flightEnd;
  } else if (explicit && fullEnd !== flightEnd && timeframe.start === flightStart &&
      (timeframe.end === fullEnd || preset === "full_delivery")) {
    effectivePreset = "full_delivery";
    bound = fullEnd;
  } else if (explicit && ((timeframe.start === flightStart && timeframe.end === flightEnd) || preset === "flight")) {
    effectivePreset = "flight";
    bound = flightEnd;
  }
  if (!bound) return requested;
  const today = canonicalReportingDay(now);
  const end = bound < today ? bound : today;
  if (explicit && flightStart > end) return requested;
  return { ...requested, start: flightStart, end, datePreset: effectivePreset,
    key: `${effectivePreset}:${flightStart}:${end}`, timeZone: REPORTING_TIME_ZONE,
    endsByTimeZone: reportingEndsByTimeZone(bound, now) };
}

// Match the Backend advertiser-scoped name key, rather than presentation names
// (which can prefer external provider labels). Ambiguous/missing names have no
// selected identity and must never become an advertiser/portfolio request.
const campaignNameKey = value => typeof value === "string" ? value.trim().toLowerCase()
  .replace(/[_-]+/g, " ").replace(/[^a-z0-9 ]+/g, "").replace(/\s+/g, " ").trim() : "";
export function resolveReportingCampaignSelection({ campaigns = [], advertiserId, campaignId, campaignName } = {}) {
  const validIdentity = value => (typeof value === "number" ||
      (typeof value === "string" && /^\d+$/.test(value.trim()))) &&
    Number.isSafeInteger(Number(value)) && Number(value) > 0;
  const id = validIdentity(campaignId) ? Number(campaignId) : null;
  if (campaignId != null && campaignId !== "" && id == null) return { id: null, campaign: null, unavailable: true };
  const wantedName = campaignNameKey(campaignName);
  const rows = (Array.isArray(campaigns) ? campaigns : []).filter(row => {
    const scope = row?.advertiser_id ?? row?.advertiserId;
    return row && typeof row === "object" && (scope == null || (validIdentity(scope) && String(scope) === String(advertiserId)));
  });
  if (id != null) return { id, campaign: rows.find(row => String(row.id ?? row.campaign_id) === String(id)) || null, unavailable: false };
  if (!wantedName) return { id: null, campaign: null, unavailable: campaignName != null && campaignName !== "" };
  const matches = rows.filter(row => campaignNameKey(row.campaign_name || row.campaignName) === wantedName);
  const identities = [...new Set(matches.map(row => row.id ?? row.campaign_id).filter(value =>
    validIdentity(value)).map(String))];
  if (identities.length !== 1) return { id: null, campaign: null, unavailable: true };
  const matched = matches.find(row => String(row.id ?? row.campaign_id) === identities[0]);
  return { id: matched.id ?? matched.campaign_id, campaign: matched, unavailable: false };
}


// A reported zero is not a truthy delivery signal. Only the canonical snapshot
// for this exact requested scope can establish it; latest-window fallback and
// another campaign/channel cannot grant availability to the selected window.
export function hasReportedSnapshotZero(snapshot, { advertiserId, campaignId, channel, timeframe } = {}) {
  if (snapshot?.found !== true || snapshot?.snapshot?.sourceContract?.contract !== "canonical_reporting_v1") return false;
  if (!snapshotScopeMatches(snapshot, { advertiserId, campaignId, channel, timeframe })) return false;
  const sameIdentity = (actual, expected) =>
    ["string", "number"].includes(typeof actual) && String(actual).trim() !== "" &&
    String(actual) === String(expected);
  if (!sameIdentity(snapshot.advertiserId, advertiserId) || canonicalSnapshotChannel(snapshot.channel) !== canonicalSnapshotChannel(channel)) return false;
  const selectedCampaign = campaignId != null && String(campaignId).trim() !== "";
  if (selectedCampaign ? !sameIdentity(snapshot.campaignId, campaignId) : snapshot.campaignId != null) return false;
  if (!timeframe?.start || !timeframe?.end || snapshot.window?.start !== timeframe.start || snapshot.window?.end == null ||
      !windowEndMatches(snapshot.window.end, timeframe, answerTimeZone(snapshot))) return false;
  const expectedScope = selectedCampaign ? "campaign" : "active_campaigns";
  if (snapshot.snapshot.sourceContract.scope != null && snapshot.snapshot.sourceContract.scope !== expectedScope) return false;
  for (const summary of [snapshot.canonicalSummary, snapshot.canonicalReporting]) {
    if (!summary || typeof summary !== "object") continue;
    if (summary.advertiserId != null && !sameIdentity(summary.advertiserId, advertiserId)) return false;
    if (selectedCampaign ? (summary.campaignId != null && !sameIdentity(summary.campaignId, campaignId)) : summary.campaignId != null) return false;
    if (summary.timeframe && (summary.timeframe.start !== timeframe.start || summary.timeframe.end == null ||
        !windowEndMatches(summary.timeframe.end, timeframe, summary.timeframe.timeZone ?? answerTimeZone(snapshot)))) return false;
  }
  return hasReportedMetricZero(snapshot.kpis);
}

export function hasReportedMetricZero(metrics) {
  if (!metrics || typeof metrics !== "object" || Array.isArray(metrics) || metrics.hasReportedDeliveryData !== true) return false;
  const fields = ["billedSpend", "clientFacingSpend", "spend", "impressions", "clicks",
    "conversions", "engagements", "trueViewViews", "videoViews", "video_views",
    "videoStarted", "video_started", "videoStarts", "videoCompletions", "video_completed"];
  return fields.some(key => {
    const value = metrics[key];
    return (typeof value === "number" || (typeof value === "string" && value.trim() !== "")) && Number.isFinite(Number(value)) && Number(value) === 0;
  }) || readReportedCompletionCount(metrics.videoPlayedTo100Count) === 0;
}

// Older advertiser-wide positive snapshots may omit scope fields. Selected
// legacy rows require actual stored identity; supplied identity/window evidence
// must be consistent in every scope, independently of the echoed request.
export function snapshotScopeMatches(snapshot, { advertiserId, campaignId, channel, timeframe } = {}) {
  if (!snapshot || typeof snapshot !== "object") return true;
  const conflicts = (actual, expected) => actual != null && (
    !["number", "string"].includes(typeof actual) || String(actual).trim() === "" || String(actual) !== String(expected)
  );
  const selectedCampaign = campaignId != null && String(campaignId).trim() !== "";
  const campaignConflicts = actual => selectedCampaign ? conflicts(actual, campaignId) : actual != null;
  // A window must state an accepted reporting calendar, and every window in one
  // answer states the same one. Its end is either the requested end (an echoed
  // explicit range) or, for a window the backend clamped to its own "today",
  // the end for the calendar the window STATES — never another calendar's.
  // A window that states no zone (the snapshot window) was resolved on the
  // calendar its own answer's canonical evidence states — or, when the answer
  // states none, on a calendar the portal cannot know (see windowEndMatches).
  const statedZones = new Set();
  const answerZone = answerTimeZone(snapshot);
  const windowConflicts = actual => {
    if (!actual) return false;
    if (actual.timeZone != null) {
      if (!isAcceptedReportingTimeZone(actual.timeZone)) return true;
      statedZones.add(actual.timeZone);
      if (statedZones.size > 1) return true;
    }
    if (!timeframe?.start || !timeframe?.end) return false;
    return conflicts(actual.start, timeframe.start) ||
      !windowEndMatches(actual.end, timeframe, actual.timeZone ?? answerZone);
  };
  const expectedScope = selectedCampaign ? "campaign" : "active_campaigns";
  if (conflicts(snapshot.advertiserId, advertiserId) || campaignConflicts(snapshot.campaignId)) return false;
  if (snapshot.channel != null && (typeof snapshot.channel !== "string" || snapshot.channel.trim() === "" || canonicalSnapshotChannel(snapshot.channel) !== canonicalSnapshotChannel(channel))) return false;
  if (windowConflicts(snapshot.window) || conflicts(snapshot.snapshot?.sourceContract?.scope, expectedScope)) return false;
  const canonicalCoverage = snapshot.snapshot?.sourceContract?.contract === "canonical_reporting_v1";
  if (canonicalCoverage && (
      snapshot.advertiserId == null || snapshot.channel == null ||
      (selectedCampaign && snapshot.campaignId == null) ||
      snapshot.snapshot.sourceContract.scope !== expectedScope ||
      !dateOnly(snapshot.window?.start) || !dateOnly(snapshot.window?.end) ||
      snapshot.window.start > snapshot.window.end)) return false;
  const sourceScope = snapshot.snapshot?.sourceScope;
  if (selectedCampaign && snapshot.found === true && !canonicalCoverage && !sourceScope) return false;
  if (sourceScope != null) {
    if (sourceScope.contract !== "stored_snapshot_scope_v1" ||
        conflicts(sourceScope.advertiserId, advertiserId) || sourceScope.advertiserId == null ||
        (selectedCampaign && (conflicts(sourceScope.campaignId, campaignId) || sourceScope.campaignId == null)) ||
        canonicalSnapshotChannel(sourceScope.channel) !== canonicalSnapshotChannel(channel)) return false;
    const sourceStart = dateOnly(sourceScope.window?.start);
    const sourceEnd = dateOnly(sourceScope.window?.end);
    const start = dateOnly(timeframe?.start);
    const end = dateOnly(timeframe?.end);
    if (!sourceStart || !sourceEnd || sourceStart > sourceEnd ||
        (selectedCampaign && (!start || !end)) ||
        (start && sourceStart < start) || (end && sourceEnd > end)) return false;
  }
  for (const summary of [snapshot.canonicalSummary, snapshot.canonicalReporting]) {
    if (!summary || typeof summary !== "object") continue;
    if (conflicts(summary.advertiserId, advertiserId) || campaignConflicts(summary.campaignId) ||
        conflicts(summary.scope, expectedScope) || windowConflicts(summary.timeframe)) return false;
  }
  return true;
}

// The canonical summary endpoint receives explicit start/end, not the snapshot
// preset. Its explicit range contract is preserved without flight remapping.
export function canonicalReportingScopeMatches(summary, scope) {
  const selected = scope?.campaignId != null;
  if (!summary || summary.advertiserId == null ||
      summary.scope !== (selected ? "campaign" : "active_campaigns") ||
      (selected && summary.campaignId == null) ||
      !dateOnly(summary.timeframe?.start) || !dateOnly(summary.timeframe?.end) ||
      summary.timeframe.start > summary.timeframe.end ||
      !isAcceptedReportingTimeZone(summary.timeframe.timeZone)) return false;
  return snapshotScopeMatches({ advertiserId: summary?.advertiserId,
    campaignId: summary?.campaignId, window: summary?.timeframe,
    snapshot: { sourceContract: { scope: summary?.scope } }, canonicalSummary: summary }, scope);
}
